import React, { PropsWithChildren } from 'react';

import { useTranslation } from 'react-i18next';

import { Tooltip } from '@/components/floating/Tooltip.tsx';
import { Glycemia } from '@/models/diabetes/GlycemiaModel.ts';
import {
  SvgDataTooltip,
  SvgDataTooltipFooter,
  SvgDataTooltipSubtitle,
  SvgDataTooltipTitle,
} from '@/uiKit/atoms/svg/SvgDataTooltip.tsx';
import { formatNumber } from '@/utils/mathUtils.ts';

export type GlycemiaGraphTooltipProps = PropsWithChildren<{
  glycemia: Glycemia;
}>;

const GlycemiaTooltipContent: React.FC<{
  glycemia: Glycemia;
}> = ({ glycemia }) => {
  const { t } = useTranslation();

  return (
    <SvgDataTooltip>
      <SvgDataTooltipTitle title={`${formatNumber(glycemia.value, 0)} mg/dL`} />
      <SvgDataTooltipSubtitle subtitle={t('glycemia.title')} />
      <SvgDataTooltipFooter
        date={glycemia.date}
        inputMethod={glycemia.deviceType}
      />
    </SvgDataTooltip>
  );
};

export const GlycemiaGraphTooltip: React.FC<GlycemiaGraphTooltipProps> = ({
  glycemia,
  children,
}) => {
  return (
    <Tooltip
      svg
      placement="top"
      dismiss={false}
      autoOpen
      offset={0}
      content={<GlycemiaTooltipContent glycemia={glycemia} />}
    >
      {children}
    </Tooltip>
  );
};

export type GlycemiaPointTooltipProps = PropsWithChildren<{
  glycemia: Glycemia;
  x?: number;
  y?: number;
}>;

export const GlycemiaPointTooltip: React.FC<GlycemiaPointTooltipProps> = ({
  glycemia,
  x,
  y,
  children,
}) => {
  return (
    <Tooltip
      svg
      placement="top"
      relativeClientPoint
      clientPoint={{
        enabled: true,
        x: x ?? 0,
        y: y ?? 0,
      }}
      content={<GlycemiaTooltipContent glycemia={glycemia} />}
    >
      {children}
    </Tooltip>
  );
};
